import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CodeBlock,
  Icon,
  Link,
  Text,
  tokens,
  Tooltip,
  useCopyToClipboard,
} from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { OVERVIEW } from '@hotjar/webapp/constants/routes';
import { useSiteName } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import { useUserPersonalizationInfoQuery } from '@hotjar/webapp/domains/activation/user-personalization-info/query';
import { USER_GOALS } from '@hotjar/webapp/domains/activation/user-personalization-info';
import { Code } from '@hotjar/webapp/components/Code';
import { STEPS } from '../../../SetupWizard/steps';
import { getTrackingCode } from '../../../TrackingCodeSnippet/trackingCode';

const StyledAlert = styled(Alert)`
  min-height: 0;
  margin-bottom: ${tokens.spacer.x2};
  padding: 6px ${tokens.spacer.x1pt5};

  p {
    color: ${tokens.color.text_light};
  }
`;

const HeroWrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  background-color: ${tokens.color.cobalt_100};
  padding: ${tokens.spacer.x3} ${tokens.spacer.x2pt5} ${tokens.spacer.x2} ${tokens.spacer.x3};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.spacer.x1};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 345px;
    height: 345px;
    border-radius: 345px;
    transform: translate(50%, -50%);
    background: radial-gradient(
      105% 117.79% at 48.77% 80.34%,
      #a18fd6 8.43%,
      #c3f0f7 58.85%,
      rgba(195, 240, 247, 0) 100%
    );
    filter: blur(123px);
    opacity: 0.4;
  }
`;

const SiteIdContainer = styled.div`
  position: absolute;
  top: ${tokens.spacer.x2pt5};
  right: ${tokens.spacer.x2pt5};
`;

const Capitalize = styled.span`
  text-transform: capitalize;
`;

const HeroCopyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-left: ${tokens.spacer.x2};
`;

const InstallationIcon = styled(Icon)<{ isIconRed?: boolean }>`
  color: ${({ isIconRed }) => (isIconRed ? tokens.color.ember_600 : tokens.color.primary_cta)};
`;

const SideStepsLine = styled.div`
  position: absolute;
  top: ${tokens.spacer.x3};
  left: -32px;
  width: 1px;
  height: calc(100% - ${tokens.spacer.x3});
  background-color: ${tokens.color.ui_border};
`;

const MainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const StepContainer = styled.div``;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${tokens.spacer.x2pt5};
`;

const StepNumber = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: -43px;
  width: ${tokens.spacer.x3};
  height: ${tokens.spacer.x3};
  background-color: ${tokens.color.ui_background};
  border: 1px solid ${tokens.color.ui_border_alt};
  border-radius: ${tokens.spacer.x3};
  color: ${tokens.color.text_light};
`;

const StyledCodeBlock = styled(CodeBlock)`
  margin-top: ${tokens.spacer.x1};
  background-color: ${tokens.color.ui_background};

  & button {
    background-color: ${tokens.color.ui_background};
  }

  & div {
    padding: 8px;
    background-color: ${tokens.color.cobalt_200};
  }

  & pre {
    padding: 8px 16px;
  }

  & code {
    color: ${tokens.color.text_light};
  }

  & code,
  & span {
    line-height: 17px;
  }
`;

const StyledCode = styled(Code)`
  background-color: ${tokens.color.dusk_400};
  color: ${tokens.color.text_light};
`;

const CodeBlockContainer = styled.div``;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacer.x2};
  margin-top: ${tokens.spacer.x1pt5};
`;

const VerifyButton = styled(Button)`
  display: block;
  width: fit-content;
  margin-top: ${tokens.spacer.x1};
`;

const HeadTag = () => <StyledCode>&lt;head&gt;</StyledCode>;

export const GuidedSetupCard = ({ site }: { site: Site }): JSX.Element => {
  const { t } = useTranslation();
  const track = useTrack();
  const { show: showTrackingCodeModal } = useModalTrackingCode();
  const { data: userPersonalizationInfo } = useUserPersonalizationInfoQuery();
  const siteName = useSiteName(site);
  const { goal } = userPersonalizationInfo || {};
  const hasGoal = !!goal && goal !== USER_GOALS.SKIPPED;
  const { isCopied, copy } = useCopyToClipboard(getTrackingCode(siteName, site.id));

  const onFinishInstallClick = () => {
    track.click('manual installation', 'Finish Install', {});
  };

  const onOtherInstallMethodsClick = () => {
    track.click('manual installation', 'Other Install Methods', {});
  };

  const onVerifyClick = () => {
    showTrackingCodeModal(site, 2, 'Setup Hotjar Component');
    track.click('manual installation', 'Type of Installation', {});
  };

  return (
    <>
      <StyledAlert kind="warning">
        <Trans i18nKey="activation:space-overview.guided-setup.install-hotjar-alert">
          You’ll see recordings and heatmaps data after you install the tracking code.
          <Link
            onClick={onFinishInstallClick}
            href={`${getRoute(OVERVIEW.SETUP.path, {
              siteId: site.id,
            })}?step=${STEPS.INSTALLATION_METHOD}`}
            kind="inline"
            data-testid="finish-install-cta"
          >
            Finish install
          </Link>
        </Trans>
      </StyledAlert>
      <HeroWrapper data-testid="hero-section">
        <InstallationIcon icon="code" size="l" />
        <SiteIdContainer>
          <Tooltip text={t('activation:space-overview.guided-setup.site-id-tooltip')}>
            <Text textType="body2">
              <Trans i18nKey="activation:tracking-code.site-id" values={{ siteId: site.id }}>
                Site ID:
                <b>{{ siteId: site.id }}</b>
              </Trans>
            </Text>
          </Tooltip>
        </SiteIdContainer>
        <HeroCopyWrapper>
          <Text textType="header4" textColor="text_default" as="h2">
            {hasGoal ? (
              <Trans i18nKey="activation:space-overview.guided-setup.install-hotjar-to-use-tool">
                To use <Capitalize>{{ goal }}</Capitalize>, install Hotjar on your site
              </Trans>
            ) : (
              <Trans i18nKey="activation:space-overview.guided-setup.install-hotjar">
                Install Hotjar on your site
              </Trans>
            )}
          </Text>
          <MainContainer>
            <SideStepsLine />
            <StepContainer>
              <StepRow>
                <StepNumber>1</StepNumber>
                <Text>{t('activation:space-overview.guided-setup.copy-this-code')}</Text>
              </StepRow>
              <CodeBlockContainer>
                <StyledCodeBlock content={getTrackingCode(siteName, site.id)} />
                <ButtonWrapper>
                  <Button
                    onClick={copy}
                    kind="secondary"
                    size="s"
                    icon={isCopied ? 'check' : 'copy'}
                    data-testid="copy-tracking-code-cta"
                  >
                    {isCopied
                      ? t('activation:space-overview.guided-setup.copied')
                      : t('activation:space-overview.guided-setup.copy-code')}
                  </Button>
                  <Button
                    as="a"
                    kind="ghost"
                    href={`${getRoute(OVERVIEW.SETUP.path, {
                      siteId: site.id,
                    })}?step=${STEPS.INSTALLATION_METHOD}`}
                    data-testid="other-install-methods-cta"
                    onClick={onOtherInstallMethodsClick}
                    size="s"
                  >
                    {t('activation:space-overview.tracking-code.secondary-cta')}
                  </Button>
                </ButtonWrapper>
              </CodeBlockContainer>
            </StepContainer>
            <StepContainer>
              <StepRow>
                <StepNumber>2</StepNumber>
                <Text>
                  <Trans i18nKey="activation:space-overview.guided-setup.paste-code-into-the-head">
                    Paste the code into the <HeadTag /> of every page where you want to track user
                    behavior or collect feedback.
                  </Trans>
                </Text>
              </StepRow>
            </StepContainer>
            <StepContainer>
              <StepRow>
                <StepNumber>3</StepNumber>

                <Text>
                  {t('activation:space-overview.guided-setup.verify-the-code-was-installed')}
                </Text>
              </StepRow>
            </StepContainer>
          </MainContainer>
          <VerifyButton
            onClick={onVerifyClick}
            loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            kind="secondary"
            size="s"
            data-testid="verify-installation-cta"
          >
            {t('activation:tracking-code.installation.verify')}
          </VerifyButton>
        </HeroCopyWrapper>
      </HeroWrapper>
    </>
  );
};
